import { createApi } from "@reduxjs/toolkit/query/react";
import { ORDER, TagId, TagType } from "../../configs";
import {
  Order,
  OrderStatus,
  PaginatedResponse,
  PaginationQuery,
  OrderDetails,
  RushOrders,
  HttpMethod,
  RushOrder,
  RushOrderType,
} from "../../types";
import { createBaseQuery } from "../helpers/baseQueryHelper";

interface SearchOrdersQuery extends PaginationQuery {
  cityId?: string;
  displayId?: string;
  restaurantId?: string;
  status?: OrderStatus;
  startDate?: string;
  endDate?: string;
}

interface SearchRushOrdersQuery extends PaginationQuery {
  rushType: RushOrderType;
}

const orderApi = createApi({
  reducerPath: ORDER.reducerPath,
  baseQuery: createBaseQuery(ORDER.baseUrl),
  tagTypes: ORDER.tagTypes,
  endpoints(builder) {
    return {
      updateOrderStatus: builder.mutation<
        void,
        { orderId: string; status: OrderStatus }
      >({
        query: ({ orderId, status }) => ({
          url: `/${orderId}/status`,
          method: HttpMethod.PATCH,
          params: { status },
        }),
        invalidatesTags: (_result, _error, { orderId }) => [
          { type: TagType.Order, id: TagId.LIST },
          { type: TagType.Order, id: orderId },
        ],
      }),

      updateOrderRider: builder.mutation<
        void,
        { orderId: string; riderId: string }
      >({
        query: ({ orderId, riderId }) => ({
          url: `/${orderId}/rider`,
          method: HttpMethod.PATCH,
          params: { riderId },
        }),
        invalidatesTags: (_result, _error, { orderId }) => [
          { type: TagType.Order, id: TagId.LIST },
          { type: TagType.Order, id: orderId },
        ],
      }),

      fetchOrders: builder.query<PaginatedResponse<Order>, SearchOrdersQuery>({
        query: (searchQuery) => {
          return {
            url: "/admin",
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Order, id: TagId.LIST }],
      }),

      fetchRushOrders: builder.query<RushOrders, void>({
        query: () => {
          return {
            url: "/rush",
            method: HttpMethod.GET,
          };
        },
        providesTags: () => [{ type: TagType.Order, id: TagId.RUSH_COUNT }],
      }),

      fetchRushOrdersList: builder.query<
        PaginatedResponse<RushOrder>,
        SearchRushOrdersQuery
      >({
        query: (searchQuery) => {
          return {
            url: "/rush/details",
            method: HttpMethod.GET,
            params: searchQuery,
          };
        },
        providesTags: () => [{ type: TagType.Order, id: TagId.LIST }],
      }),

      fetchOrderDetails: builder.query<OrderDetails, string>({
        query: (orderId) => {
          return {
            url: `/${orderId}/admin`,
            method: HttpMethod.GET,
          };
        },
        providesTags: (_result, _error, orderId) => [
          { type: TagType.Order, id: orderId },
        ],
      }),
    };
  },
});

export const {
  useFetchOrdersQuery,
  useFetchRushOrdersQuery,
  useFetchOrderDetailsQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderRiderMutation,
  useFetchRushOrdersListQuery,
} = orderApi;

export { orderApi };
